/**
 * bankService — canonical Bank Accounts API client.
 *
 * Purpose:
 *   Every page that lists bank accounts MUST import from this module
 *   instead of hard-coding the endpoint path. If the backend prefix
 *   ever changes (`/bank/accounts` → `/banking/accounts` etc.) it is
 *   a one-line edit here, not a hunt across 8 pages.
 *
 * Historical bug (July 2026):
 *   `GovUtilityPayments.jsx` shipped with `/bank-accounts` (missing
 *   the `/` — should have been `/bank/accounts`). It silently 404'd
 *   and left the "Paid from Bank" dropdown empty. That class of bug
 *   is now unrepresentable — you can only reach the API through
 *   `bankService.listAccounts()`.
 *
 * Return-shape contract:
 *   `listAccounts()` resolves to `{ ok, accounts, error }` so callers
 *   can distinguish three states without stringly-typed catch blocks:
 *
 *     ok=true,  accounts=[…]   → success, may be empty
 *     ok=true,  accounts=[]    → success, tenant has no accounts
 *     ok=false, error=Error    → network / 4xx / 5xx / parse failure
 *
 *   UI code decides between "No active bank accounts" (empty success)
 *   and "Unable to load bank accounts" (fetch failure).
 */
import { axiosInstance } from '../App';


const ENDPOINT = '/bank/accounts';


/** Normalise the various shapes the backend has returned historically. */
function _asList(data) {
  if (Array.isArray(data)) return data;
  if (!data) return [];
  return data.items || data.accounts || [];
}


/**
 * List bank accounts for the active tenant.
 * @returns {Promise<{ok: boolean, accounts: Array, error: Error|null}>}
 */
async function listAccounts() {
  try {
    const { data } = await axiosInstance.get(ENDPOINT);
    return { ok: true, accounts: _asList(data), error: null };
  } catch (err) {
    return { ok: false, accounts: [], error: err };
  }
}


/**
 * Human-friendly one-line label for a bank account row. Falls back
 * gracefully when optional fields are missing so the SelectItem never
 * renders `undefined · undefined`.
 */
function formatLabel(b) {
  if (!b) return '';
  const parts = [];
  parts.push(b.bank_name || b.name || 'Bank');
  if (b.account_name) parts.push(`— ${b.account_name}`);
  const ref = b.account_number || b.iban;
  if (ref) parts.push(`· ${ref}`);
  return parts.join(' ');
}


export const bankService = {
  listAccounts,
  formatLabel,
  // Exposed so tests / debug tooling can assert the canonical path
  // without stringly-typed knowledge of the module.
  _endpoint: ENDPOINT,
};

export default bankService;
